/**
 * Local shared functions service
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class WdLocalSharedFunctionsService {
  // Banner image, page title and subtitle
  bannerImage = 'assets/images/banner/bcx-login-banner.png';
  bannerImageLoaded = false;
  pageTitle: string;
  pageSubtitle: string;
  useBanner = false;

  /**
   * Constructor
   */
  constructor(public translateService: TranslateService) {}

  /**
   * Handle errors returned from oauth2-server and get error text
   */
  getErrorMessage(
    response: { statusType?: string; statusText?: string },
    key: string,
  ): string {
    // Default to a message based on the given key
    let errorMessage = this.translateService.instant('errors.' + key);
    // Handle or translate errors on a case by case basis
    if (response.statusType && response.statusText) {
      // @todo Refactor oauth2-server to consistently return an error ID in the status.label field and handle below
      switch (response.statusType) {
        // Expired forgotten password link
        case 'expiredInvalidForgetPasswordToken':
          errorMessage = this.translateService.instant(
            'errors.' + response.statusType,
          );
          break;
        // Use the default message returned from the server
        default:
          errorMessage = response.statusText;
      }
    }
    return errorMessage;
  }
  /**
   * Handle focus events
   * Only allow focus on inputs and buttons
   */
  handleFocus(event: FocusEvent) {
    const target = event.target as HTMLElement;

    // Define the elements that should retain focus
    const allowedTags = ['INPUT', 'BUTTON', 'TEXTAREA', 'SELECT'];

    // If the clicked element is not an allowed element, prevent it from pulling focus
    if (!allowedTags.includes(target.tagName)) {
      event.preventDefault();
      // Blur and refocus the currently focussed element to allow closing autofill windows by clicking away from them
      const focusedElement = document.activeElement as HTMLElement;
      // Temporarily blur the original element
      focusedElement.blur();
      // Immediately refocus the original element
      focusedElement.focus();
    }
  }
  /**
   * Handle tab event for element
   */
  handleTabEvent(
    event: KeyboardEvent,
    currentElement: HTMLInputElement,
    controlsArray: HTMLInputElement[],
  ) {
    // Prevent the default tab behavior
    event.preventDefault();
    // Loop to find the next available (enabled) control
    for (let i = 1; i <= controlsArray.length; i++) {
      const nextIndex =
        (controlsArray.indexOf(currentElement) + i) % controlsArray.length;
      const nextControl = controlsArray[nextIndex];

      // Focus the next available control
      if (!nextControl.disabled) {
        nextControl.focus();
        return;
      }
    }
  }
}
